<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { SliderRoot, SliderTrack, SliderRange, SliderThumb } from 'radix-vue'
import WrapperSvg from './assets/wrapper.svg?skipsvgo'
import FillSvg from './assets/fill.svg'
import ThumbSvg from './assets/thumb.svg?skipsvgo'

const props = defineProps<{
  min: number
  max: number
}>()

const emit = defineEmits<{
  (e: 'done'): void
}>()

const modelValue = defineModel<number[]>()

const rangeList = computed<number[]>(() => {
  const values: number[] = []
  if (props.min > 0) values.push(0)
  for (let v = props.min; v <= props.max; v += 1) values.push(v)
  return values
})
const rangeMax = computed(() => rangeList.value.length - 1)

const indexValue = ref<number[]>([0])
const isBootstrapped = ref(false)

watch(
  modelValue,
  (val) => {
    const current = val?.[0]
    if (typeof current !== 'number') return
    const idx = rangeList.value.indexOf(current)
    indexValue.value = [idx >= 0 ? idx : 0]
    isBootstrapped.value = true
  },
  { immediate: true, deep: true }
)

watch(
  indexValue,
  (val) => {
    if (!isBootstrapped.value) return
    const idx = val?.[0]
    if (typeof idx !== 'number') return
    const newAmount = rangeList.value[idx]
    if (modelValue.value?.[0] !== newAmount) modelValue.value = [newAmount]
  },
  { deep: true }
)

const isFocused = ref(false)

const gotFocus = () => {
  isFocused.value = true
}
const lostFocus = () => {
  isFocused.value = false
}
</script>
<template>
  <div
    class="raise-slider"
    :class="{
      'raise-slider--active': isFocused
    }"
  >
    <div
      class="raise-slider__wrap"
      :class="{
        'raise-slider__wrap--active': isFocused
      }"
    >
      <wrapper-svg style="position: absolute; left: 0; bottom: 0" />
      <div
        class="raise-slider__value"
        :class="{
          'raise-slider__value--all-in': modelValue?.[0] === props.max
        }"
      >
        {{ modelValue?.[0] }}
      </div>
    </div>
    <SliderRoot
      v-model="indexValue"
      :min="0"
      :max="rangeMax"
      :step="1"
      class="raise-slider__root"
      :class="{
        'raise-slider__root--active': isFocused
      }"
      orientation="vertical"
    >
      <SliderTrack class="raise-slider__track">
        <SliderRange
          class="raise-slider__range"
          :class="{
            'raise-slider__range--active': isFocused
          }"
        >
          <fill-svg class="raise-slider__fill-content" />
        </SliderRange>
      </SliderTrack>
      <SliderThumb
        class="raise-slider__thumb"
        @focus="gotFocus"
        @blur="lostFocus"
        @pointerup="emit('done')"
      >
        <thumb-svg
          class="raise-slider__thumb-content"
          :class="{
            'raise-slider__thumb-content--focus': isFocused
          }"
        />
      </SliderThumb>
    </SliderRoot>
  </div>
</template>

<style lang="scss">
.raise-slider {
  height: 80px;
  width: 94px;

  &--active {
    height: 254px;
  }

  &__wrap {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 0%;
    opacity: 0;
    transition: ease-in-out all 0.25s;
    overflow: hidden;

    &--active {
      height: 100%;
      opacity: 1;
    }
  }

  &__range {
    opacity: 0;

    &--active {
      opacity: 1;
    }
  }

  &__fill-content {
    bottom: 0px;
    position: absolute;
    width: 70px;
    height: 152px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__thumb-content {
    pointer-events: none;

    &--focus {
      #blend {
        mix-blend-mode: lighten !important;
      }

      #radial {
        fill: url(#radial_active) !important;
      }

      #linear_1 {
        stroke: url(#linear_1_active) !important;
      }
    }
  }

  &__root {
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    touch-action: none;

    width: 100%;
    bottom: 24px;
    height: 196px;

    position: absolute;
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  &__track {
    position: absolute;
    flex-grow: 1;
    width: 100%;
    height: 150px;
    bottom: 18px;
    pointer-events: none;
  }

  &__range {
    position: absolute;
    width: 100%;
    overflow: hidden;
  }

  &__thumb {
    outline: none;
  }

  &__value {
    color: #fff;
    text-align: center;
    text-shadow: 0px 4.5848px 4.5848px rgba(41, 39, 18, 0.1);
    font-family: Poppins;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.1px;

    transform: translateX(0);
    top: 16px;
    position: relative;

    &--all-in {
      color: #d4b547;
    }
  }
}
</style>
